/**
 * Misiones — Kanban (Asignadas / En curso / Completadas).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, X, Calendar, User as UserIcon, Hash, Target, Trash2 } from 'lucide-react';
import Header from './Header';
import useStore from '../store/useStore';

const PRIORITY = {
  low:      { label: 'Baja',     hue: '#34C759', tint: '#E5F9EC' },
  medium:   { label: 'Media',    hue: '#007AFF', tint: '#E6F1FF' },
  high:     { label: 'Alta',     hue: '#FF9F0A', tint: '#FFF3DE' },
  critical: { label: 'Crítica',  hue: '#FF2D55', tint: '#FFE5EC' },
};

const STATUSES = [
  { id: 'assigned',    label: 'Asignadas',   hue: '#86868B', tint: '#F5F5F7' },
  { id: 'in_progress', label: 'En curso',    hue: '#007AFF', tint: '#E6F1FF' },
  { id: 'completed',   label: 'Completadas', hue: '#34C759', tint: '#E5F9EC' },
];

function fmtETA(eta) {
  if (!eta) return null;
  const d = new Date(eta);
  const now = new Date();
  const diff = d - now;
  const days = Math.round(diff / 86400000);
  if (Math.abs(days) < 1) return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  if (days < 0) return `${Math.abs(days)}d atrás`;
  if (days === 1) return 'mañana';
  if (days < 7) return `en ${days}d`;
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

function MissionCard({ m, onPatch, onDelete }) {
  const p = PRIORITY[m.priority] || PRIORITY.medium;
  const eta = fmtETA(m.eta);
  return (
    <div className="rounded-md p-3 mb-2 shadow-apple-sm" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.05)' }}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{ background: p.tint, color: p.hue }}
        >
          {p.label}
        </span>
        <button
          onClick={() => onDelete(m.id)}
          className="text-n-300 hover:text-s-red transition-colors"
          title="Eliminar"
        >
          <Trash2 size={12} />
        </button>
      </div>
      <h4 className="text-sm font-semibold text-n-800 leading-tight tracking-apple-tight mb-1">{m.title}</h4>
      {m.description && (
        <p className="text-[11px] text-n-500 mb-2 line-clamp-2">{m.description}</p>
      )}
      <div className="flex items-center gap-2 text-[10px] text-n-500 mb-2">
        {m.assignee && (
          <div className="flex items-center gap-1">
            <div
              className="w-4 h-4 rounded-full text-[8px] font-bold text-white flex items-center justify-center"
              style={{ background: m.assignee.avatarColor || '#007AFF' }}
            >
              {m.assignee.username?.[0]?.toUpperCase()}
            </div>
            <span>{m.assignee.username}</span>
          </div>
        )}
        {m.Channel && (
          <div className="flex items-center gap-0.5"><Hash size={10} />{m.Channel.name}</div>
        )}
        {eta && (
          <div className="flex items-center gap-0.5"><Calendar size={10} />{eta}</div>
        )}
      </div>
      <select
        value={m.status}
        onChange={(e) => onPatch(m.id, { status: e.target.value })}
        className="w-full text-[11px] px-2 py-1 rounded-sm border border-n-200 text-n-700 bg-white"
      >
        {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
    </div>
  );
}

function CreateModal({ open, onClose, onCreate, users, channels }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assigneeId, setAssigneeId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [eta, setEta] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      await onCreate({
        title: title.trim(),
        description: description.trim(),
        priority,
        assigneeId: assigneeId || null,
        channelId: channelId || null,
        eta: eta || null,
      });
      setTitle(''); setDescription(''); setPriority('medium');
      setAssigneeId(''); setChannelId(''); setEta('');
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.30)', backdropFilter: 'blur(8px)' }}>
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-lg p-5 shadow-apple-lg"
        style={{ background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(0,0,0,0.06)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-n-800 tracking-apple-tight">Nueva misión</h3>
          <button type="button" onClick={onClose} className="text-n-400 hover:text-n-700"><X size={16} /></button>
        </div>
        <label className="block text-[11px] font-semibold text-n-600 mb-1">Título</label>
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Inspeccionar zona norte…"
          className="w-full text-sm px-3 py-2 rounded-sm border border-n-200 mb-3"
        />
        <label className="block text-[11px] font-semibold text-n-600 mb-1">Descripción</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Detalles…"
          className="w-full text-sm px-3 py-2 rounded-sm border border-n-200 mb-3 resize-none"
        />
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="block text-[11px] font-semibold text-n-600 mb-1">Prioridad</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-sm border border-n-200"
            >
              {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-n-600 mb-1">ETA</label>
            <input
              type="datetime-local"
              value={eta}
              onChange={(e) => setEta(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-sm border border-n-200"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div>
            <label className="block text-[11px] font-semibold text-n-600 mb-1">Asignar a</label>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-sm border border-n-200"
            >
              <option value="">— sin asignar —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-n-600 mb-1">Canal</label>
            <select
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-sm border border-n-200"
            >
              <option value="">— ninguno —</option>
              {channels.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-md text-n-600 hover:bg-black/5">Cancelar</button>
          <button
            type="submit"
            disabled={saving || !title.trim()}
            className="px-4 py-2 text-sm font-semibold text-white rounded-md shadow-apple-sm disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}
          >
            {saving ? 'Creando…' : 'Crear misión'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function MissionsPage() {
  const [missions, setMissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const channels = useStore(s => s.channels);

  const loadMissions = async () => {
    try {
      const r = await axios.get('/api/missions');
      setMissions(r.data || []);
    } catch (e) { console.warn('[Missions] load', e); }
  };

  useEffect(() => {
    Promise.all([
      loadMissions(),
      axios.get('/api/users/directory').then(r => setUsers(r.data || [])).catch(() => {}),
      axios.get('/api/channels').then(r => useStore.getState().setChannels(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const onCreate = async (data) => {
    try {
      const r = await axios.post('/api/missions', data);
      setMissions(prev => [r.data, ...prev]);
    } catch (e) { console.warn('[Missions] create', e); }
  };

  const onPatch = async (id, patch) => {
    try {
      const r = await axios.patch(`/api/missions/${id}`, patch);
      setMissions(prev => prev.map(m => m.id === id ? r.data : m));
    } catch (e) { console.warn('[Missions] patch', e); }
  };

  const onDelete = async (id) => {
    if (!confirm('¿Eliminar misión?')) return;
    try {
      await axios.delete(`/api/missions/${id}`);
      setMissions(prev => prev.filter(m => m.id !== id));
    } catch (e) { console.warn('[Missions] delete', e); }
  };

  const grouped = useMemo(() => {
    const out = { assigned: [], in_progress: [], completed: [] };
    for (const m of missions) {
      const k = STATUSES.find(s => s.id === m.status)?.id || 'assigned';
      out[k].push(m);
    }
    return out;
  }, [missions]);

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: '#FBFBFD' }}>
      <Header />
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-n-800 tracking-apple-tighter">Misiones</h1>
            <p className="text-xs text-n-500">Tablero Kanban — operaciones en terreno</p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white shadow-apple-md"
            style={{ background: 'linear-gradient(135deg,#007AFF,#AF52DE)' }}
          >
            <Plus size={14} /> Nueva
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-n-500">Cargando…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {STATUSES.map(s => (
              <div
                key={s.id}
                className="rounded-lg p-3"
                style={{ background: 'rgba(255,255,255,0.65)', border: '1px solid rgba(0,0,0,0.05)', minHeight: 200 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: s.hue }} />
                    <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: s.hue }}>
                      {s.label}
                    </span>
                  </div>
                  <span className="text-[10px] text-n-400 font-mono">{grouped[s.id].length}</span>
                </div>
                <div>
                  {grouped[s.id].length === 0 ? (
                    <div className="text-center py-6 px-3 flex flex-col items-center gap-1.5">
                      <div className="w-8 h-8 rounded-full bg-n-100 flex items-center justify-center">
                        <Target size={14} className="text-n-400" aria-hidden="true" />
                      </div>
                      <p className="text-[11px] text-n-600 font-medium">Sin {s.label.toLowerCase()}</p>
                    </div>
                  ) : (
                    grouped[s.id].map(m => (
                      <MissionCard key={m.id} m={m} onPatch={onPatch} onDelete={onDelete} />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={onCreate}
        users={users}
        channels={channels || []}
      />
    </div>
  );
}
